import { useEffect, useState } from "react";
import { api, type BriefingItemView, type CockpitBriefing } from "../api";
import { ControlPlaneOverviewPanel } from "../dashboard/ControlPlaneOverviewPanel";
import { QueuePanel } from "../dashboard/QueuePanel";
import { ResourceGovernorPanel } from "../dashboard/ResourceGovernorPanel";
import { ScopeAuthorityPanel } from "../dashboard/ScopeAuthorityPanel";
import { FindingsLedgerPanel } from "../dashboard/FindingsLedgerPanel";
import { MissionDetailDialog } from "../dashboard/MissionDetailDialog";
import { ownerConnectorStatusCopy } from "../dashboard/connectorStatusCopy";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { OrchestratorRunView } from "./OrchestratorRunView";

function cockpitErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function cockpitErrorCopy(error: unknown): string {
  const text = cockpitErrorText(error);
  if (/cockpit.*not found|briefing.*not found|status.*not found|stale.*cockpit|cockpit_state|briefing_state/i.test(text)) {
    return "Cockpit-Status ist nicht mehr aktuell. Bitte Live-Status neu laden.";
  }
  return ownerFacingGlobalError(text);
}

export function CockpitView(): JSX.Element {
  const [data, setData] = useState<CockpitBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runIdInput, setRunIdInput] = useState("");
  const [detailMissionId, setDetailMissionId] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const load = (): void => {
    setLoading(true);
    setError(null);
    api
      .briefing()
      .then(setData)
      .catch((e) => setError(cockpitErrorCopy(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="h-full overflow-auto p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Heute</h1>
          {data && (
            <div className="text-xs text-jarvis-muted mt-1">
              {data.counts.total} Einträge in {data.durationMs} ms aggregiert
            </div>
          )}
        </div>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      </div>

      {error && <div className="card p-4 text-rose-300 text-sm">{error}</div>}

      {/* CP-08 — Autonomy Control Plane Overview (System/Queue/Ressourcen/
          Missionen/OWNER ACTION REQUIRED) vor dem Tages-Briefing */}
      <ControlPlaneOverviewPanel onOpenMission={setDetailMissionId} />

      {/* CP-09 — Queue: Tabelle/Kanban/Explainability */}
      <QueuePanel onOpenMission={setDetailMissionId} />

      {/* CP-13 — Ressourcen (Governor) */}
      <ResourceGovernorPanel />

      {/* CP-14 — Scopes & Autoritaeten */}
      <ScopeAuthorityPanel />

      {/* CP-15 — Findings / Challenger (Ledger, rein lesend) */}
      <FindingsLedgerPanel />

      {detailMissionId && (
        <MissionDetailDialog missionId={detailMissionId} onClose={() => setDetailMissionId(null)} />
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
            <StatTile label="PRs" value={data.counts.prs} />
            <StatTile label="GitHub-Benachr." value={data.counts.notifications} />
            <StatTile label="Tickets" value={data.counts.tickets} />
            <StatTile label="Termine" value={data.counts.calendar} />
            <StatTile label="Mails" value={data.counts.mails} />
          </div>

          <Section title="Pull Requests" items={data.grouped.pr} empty="Keine offenen PRs." />
          <Section
            title="GitHub-Notifications"
            items={data.grouped.notification}
            empty="Keine ungelesenen Benachrichtigungen."
          />
          <Section title="IT-Simo-Tickets" items={data.grouped.ticket} empty="Keine Tickets." />
          <Section title="Termine" items={data.grouped.calendar} empty="Keine Termine." />
          <Section title="Mails" items={data.grouped.mail} empty="Keine Mails." />

          <div className="card simo-keyboard-safe-editor p-3 sm:p-4 space-y-3" aria-label="Cockpit Orchestrator Keyboard-safe Run Filter">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent">
                Orchestrator-Live (Phase 5)
              </h3>
              <span className="text-xs text-jarvis-muted">
                runId eingeben → 13 Gates live
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                className="input simo-keyboard-safe-field min-h-10 flex-1 font-mono text-base sm:text-sm"
                placeholder="runId (z.B. run-42, mock-demo) — Mock-Mode liefert immer 13 Gates"
                value={runIdInput}
                onChange={(e) => setRunIdInput(e.target.value.trim())}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && /^[A-Za-z0-9_-]{1,64}$/.test(runIdInput)) {
                    setActiveRunId(runIdInput);
                  }
                }}
              />
              <button
                className="btn-primary"
                disabled={!/^[A-Za-z0-9_-]{1,64}$/.test(runIdInput)}
                onClick={() => setActiveRunId(runIdInput)}
              >
                Verbinden
              </button>
            </div>
            <p className="text-xs text-jarvis-muted">
              runId muss <code>^[A-Za-z0-9_-]{`{1,64}`}$</code> matchen. Wenn der
              Server <code>JARVIS_ORCHESTRATOR_MOCK=true</code> hat, liefert er
              für jede runId einen simulierten 13-Gates-Lauf.
            </p>
            {activeRunId && (
              <OrchestratorRunView
                key={activeRunId}
                runId={activeRunId}
                onClose={() => setActiveRunId(null)}
              />
            )}
          </div>

          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
              Connector-Status
            </h3>
            <div className="space-y-1 text-sm">
              {data.connectors.map((c) => {
                const copy = ownerConnectorStatusCopy(c);
                return (
                  <div
                    key={c.id}
                    className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 border-b border-jarvis-border last:border-0 py-3 sm:grid-cols-[auto_minmax(0,1fr)_minmax(10rem,1.4fr)] sm:items-center"
                  >
                    <span
                      className={`mt-1.5 inline-block w-2 h-2 rounded-full sm:mt-0 ${
                        c.reachable
                          ? "bg-emerald-400"
                          : c.configured
                            ? "bg-amber-400"
                            : "bg-rose-400"
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="font-medium leading-snug">{c.name}</div>
                      <div className="text-jarvis-muted text-xs font-mono break-all">{c.id}</div>
                    </div>
                    <span className="col-start-2 text-xs text-jarvis-muted leading-relaxed break-words sm:col-start-auto sm:text-right">
                      <strong className="text-jarvis-text">{copy.statusLabel}</strong>
                      <span className="block">{copy.message}</span>
                      <span className="block">Fähigkeit: {copy.capability}</span>
                      <span className="block">Aktion: {copy.action}</span>
                      <span className="block">Sicher: {copy.safeCta}</span>
                      <span className="block">Details: {copy.detailsCta}</span>
                      <span className="block">Owner-Pfad: {copy.ownerPath}</span>
                      <span className="block">Evidence: {copy.evidence}</span>
                      {copy.risk && <span className="block text-amber-200/80">Risiko: {copy.risk}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="card p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs text-jarvis-muted uppercase tracking-wider">{label}</div>
      <div className="text-2xl sm:text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function Section({
  title,
  items,
  empty,
}: {
  title: string;
  items: BriefingItemView[];
  empty: string;
}): JSX.Element {
  return (
    <div className="card p-3 sm:p-4">
      <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
        {title}
      </h3>
      {items.length === 0 ? (
        <div className="text-sm text-jarvis-muted">{empty}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <a
              key={`${it.connector}-${it.title}-${idx}`}
              href={it.url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 py-2 border-b border-jarvis-border last:border-0 hover:bg-jarvis-bg/40 -mx-2 px-2 rounded transition-colors"
            >
              <span
                className={`inline-block w-1 h-10 rounded-full ${
                  it.priority === "high"
                    ? "bg-rose-400"
                    : it.priority === "medium"
                      ? "bg-amber-400"
                      : "bg-jarvis-border"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.title}</div>
                {it.subtitle && (
                  <div className="text-xs text-jarvis-muted font-mono truncate">{it.subtitle}</div>
                )}
              </div>
              {it.timestamp && (
                <div className="text-xs text-jarvis-muted whitespace-nowrap">
                  {new Date(it.timestamp).toLocaleString("de-DE")}
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
